import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Download,
  Save,
  X,
  FileText,
  DollarSign,
  Calendar,
  User,
  CheckCircle,
  Clock,
  XCircle,
  ShoppingCart,
  FileDown
} from 'lucide-react';
import { quotesAPI, clientsAPI } from '../services/api';
import { generateQuotePDF } from '../utils/devisPDF';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { productsAPI } from '../services/api';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';
import AddProductForm from '../components/forms/AddProductForm';

// 1. Add items array to form state
const defaultForm = {
  client_id: '',
  amount: '',
  date: '',
  status: 'pending',
  note: '',
  items: [], // Array of { product_id, item_type, side, quantity, price }
};

const VentesDevis = () => {
  // All useState/useEffect hooks are at the very top, before any logic or return
  // No hooks are called inside any function, conditional, or after a return
  // No inline component or custom hook definitions inside the main component
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [itemForm, setItemForm] = useState({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
  const [editIdx, setEditIdx] = useState(null);
  const [itemError, setItemError] = useState('');

  // 1. Add state for details modal
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedDevis, setSelectedDevis] = useState(null);

  // Add a loading state for details/edit fetch
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Add missing modal-related state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('monture');
  const [modalEditIdx, setModalEditIdx] = useState(null);
  const [modalForm, setModalForm] = useState({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
  const [modalError, setModalError] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  
  // Add product modal state
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // Handle product creation success
  const handleProductCreated = async (newProduct) => {
    try {
      // Refresh products list
      const response = await productsAPI.getProducts();
      setProducts(response.data);
      // Close the modal
      setShowAddProductModal(false);
      // Show success message
      toast.success('Produit ajouté avec succès');
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };

  const filterProducts = (searchTerm) => {
    if (!searchTerm.trim()) {
      return [];
    }
    
    const filtered = products.filter(product => {
      const name = product?.name || '';
      const model = product?.model || '';
      const brand = product?.brand || '';
      return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             model.toLowerCase().includes(searchTerm.toLowerCase()) ||
             brand.toLowerCase().includes(searchTerm.toLowerCase());
    });
    return filtered;
  };

  // Place useEffect here, before any logic or return
  useEffect(() => {
    if (modalOpen) {
      setProductSearch('');
      setSelectedProductIds([]);
    }
  }, [modalOpen, modalType]);

  // 2. Helper to add item
  const addItem = (item) => setForm(f => ({ ...f, items: [...f.items, item] }));
  const updateItem = (idx, newItem) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? newItem : it) }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // Fetch quotes
  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const res = await quotesAPI.getQuotes();
      setQuotes(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des devis');
    }
    setLoading(false);
  };

  // Fetch clients for dropdown
  const fetchClients = async () => {
    try {
      const res = await clientsAPI.getClients();
      setClients(res.data);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchQuotes();
    fetchClients();
    productsAPI.getProducts().then(res => setProducts(res.data)).catch(() => setProducts([]));
  }, []);

  // Handle form input
  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Fix status dropdown handler for ModernSelect
  const handleStatusChange = (option) => {
    setForm(f => ({ ...f, status: option ? option.value : '' }));
  };

  // 4. On submit, send form.items to backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      const totalAmount = form.items.reduce((sum, item) => sum + (Number(item.price) * Number(item.quantity)), 0);
      const payload = {
        client_id: form.client_id,
        amount: form.amount || totalAmount,
        date: form.date,
        status: form.status,
        note: form.note,
        items: form.items,
      };
      if (editingId) {
        await quotesAPI.updateQuote(editingId, payload);
        toast.success('Devis modifié');
      } else {
        await quotesAPI.createQuote(payload);
        toast.success('Devis ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchQuotes();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 5. On edit, load items for the quote (fetch from backend if needed)
  const handleEdit = (quote) => {
    // For now, we'll just set the form with the quote's items.
    // In a real app, you'd fetch the full quote details including items.
    setForm({
      client_id: quote.client_id,
      amount: quote.amount,
      date: quote.date ? quote.date.slice(0, 10) : '',
      status: quote.status,
      note: quote.note,
      items: quote.items || [], // Assuming quote.items contains the array of items
    });
    setEditingId(quote.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ? Cette action est irréversible.')) return;
    try {
      await quotesAPI.deleteQuote(id);
      toast.success('Devis supprimé');
      fetchQuotes();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  // Helper to get client name
  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? client.name : clientId;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'En attente' },
      accepted: { variant: 'success', label: 'Accepté' },
      rejected: { variant: 'danger', label: 'Rejeté' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <ModernBadge variant={config.variant}>{config.label}</ModernBadge>;
  };

  const filteredQuotes = quotes.filter(quote => {
    const clientName = getClientName(quote.client_id);
    return String(clientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(quote.amount || '').includes(searchTerm) ||
           String(quote.date || '').includes(searchTerm) ||
           String(quote.status || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const clientOptions = clients.map(client => ({
    value: client.id.toString(),
    label: client.name
  }));

  const statusOptions = [
    { value: 'pending', label: 'En attente' },
    { value: 'accepted', label: 'Accepté' },
    { value: 'rejected', label: 'Rejeté' }
  ];

  // Helper: options for Select
  const frameOptions = products.filter(p => p.type === 'frames' || (p.category_name && p.category_name.toLowerCase().includes('monture')))
    .map(p => ({ value: p.id, label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}` }));
  const lensOptions = products.filter(p => p.type === 'glasses' || (p.category_name && p.category_name.toLowerCase().includes('verre')))
    .map(p => ({ value: p.id, label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}` }));
  const accessoryOptions = products.filter(p => p.type === 'accessories' || (p.category_name && p.category_name.toLowerCase().includes('accessoire')))
    .map(p => ({ value: p.id, label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}` }));

  const tableColumns = [
    {
      key: 'client_id',
      label: 'Client',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <User className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{getClientName(value)}</div>
          </div>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Montant',
      render: (value) => (
        <span className="font-semibold text-success-600">
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'date',
      label: 'Date',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, quote) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => openDetailsModal(quote)}
          >
            Détails
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => openEditModal(quote)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<FileDown className="w-4 h-4" />}
            onClick={() => generateDevisPDF(quote)}
            className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700"
          >
            PDF
          </ModernButton>
          <ModernButton
            variant="success"
            size="sm"
            icon={<CheckCircle className="w-4 h-4" />}
            onClick={() => handleAcceptQuote(quote)}
            disabled={quote.status === 'accepted'}
          >
            Accepter
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<XCircle className="w-4 h-4" />}
            onClick={() => handleRejectQuote(quote)}
            disabled={quote.status === 'rejected'}
          >
            Rejeter
          </ModernButton>
          <ModernButton
            variant="info"
            size="sm"
            icon={<ShoppingCart className="w-4 h-4" />}
            onClick={() => handleConvertToSale(quote)}
            disabled={quote.status !== 'accepted'}
          >
            Convertir en vente
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(quote.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  // Only after all hooks:
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalAmount = quotes.reduce((sum, quote) => sum + (Number(quote.amount) || 0), 0);
  const pendingQuotes = quotes.filter(q => q.status === 'pending').length;
  const acceptedQuotes = quotes.filter(q => q.status === 'accepted').length;
  const rejectedQuotes = quotes.filter(q => q.status === 'rejected').length;

  // Helper to get product name
  const getProductName = (id) => {
    const p = products.find(pr => pr.id === id);
    return p ? p.name : id;
  };
  const getProductSalePrice = (id) => {
    const p = products.find(pr => pr.id === id);
    return p ? p.sale_price : 0;
  };
  const getItemTotal = (item) => {
    const price = item.price || getProductSalePrice(item.product_id);
    return price * (item.quantity || 1);
  };

  const resetItemForm = () => setItemForm({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setItemForm(f => ({ ...f, [name]: value }));
  };

  const handleAddOrUpdateItem = () => {
    if (!itemForm.product_id || !itemForm.quantity || !itemForm.price) {
      setItemError('Tous les champs sont requis');
      return;
    }
    setItemError('');
    if (editIdx !== null) {
      updateItem(editIdx, itemForm);
      setEditIdx(null);
    } else {
      // Prevent duplicate product/side/type
      if (form.items.some(i => i.product_id === itemForm.product_id && i.item_type === itemForm.item_type && i.side === itemForm.side)) {
        setItemError('Cet article existe déjà');
        return;
      }
      addItem(itemForm);
    }
    resetItemForm();
  };

  const startEditItem = (idx) => {
    setEditIdx(idx);
    setItemForm(form.items[idx]);
  };

  const cancelEditItem = () => {
    setEditIdx(null);
    resetItemForm();
  };

  // 2. Add modal state for adding/editing items
  const openModal = (type, idx = null) => {
    setModalType(type);
    setModalEditIdx(idx);
    if (idx !== null) {
      setModalForm(form.items[idx]);
    } else {
      setModalForm({ product_id: '', item_type: type, side: 'none', quantity: 1, price: '' });
    }
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalForm({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
    setModalEditIdx(null);
    setModalError('');
  };
  const handleModalChange = (field, value) => {
    setModalForm(f => ({ ...f, [field]: value }));
  };
  const handleModalSave = () => {
    if (!modalForm.product_id || !modalForm.quantity) {
      setModalError('Tous les champs sont requis');
      return;
    }
    setModalError('');
    const price = modalForm.price !== '' && modalForm.price !== undefined ? Number(modalForm.price) : getProductSalePrice(modalForm.product_id);
    const itemToSave = { ...modalForm, price };
    if (modalEditIdx !== null) {
      setForm(f => ({ ...f, items: f.items.map((it, i) => i === modalEditIdx ? itemToSave : it) }));
    } else {
      if (form.items.some(i => i.product_id === modalForm.product_id && i.item_type === modalForm.item_type && i.side === modalForm.side)) {
        setModalError('Cet article existe déjà');
        return;
      }
      setForm(f => ({ ...f, items: [...f.items, itemToSave] }));
    }
    closeModal();
  };

  // Fetch full quote details for details modal
  const openDetailsModal = async (quote) => {
    setDetailsLoading(true);
    try {
      const res = await quotesAPI.getQuote(quote.id);
      setSelectedDevis(res.data);
      setDetailsModalOpen(true);
    } catch (err) {
      toast.error('Erreur lors du chargement des détails du devis');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Fetch full quote details for edit modal
  const openEditModal = async (quote) => {
    setSubmitLoading(true);
    try {
      const res = await quotesAPI.getQuote(quote.id);
      setForm({
        client_id: res.data.client_id,
        amount: res.data.amount,
        date: res.data.date ? res.data.date.slice(0, 10) : '',
        status: res.data.status,
        note: res.data.note,
        items: res.data.items || [],
      });
      setEditingId(quote.id);
      setShowForm(true);
    } catch (err) {
      toast.error('Erreur lors du chargement du devis');
    } finally {
      setSubmitLoading(false);
    }
  };

  const calculatedTotal = form.items.reduce((sum, item) => sum + (Number(item.price) * Number(item.quantity)), 0);

  // Compute filteredProducts based on modalType and productSearch
  const filteredProducts = (modalType === 'monture' ? frameOptions : modalType === 'verre' ? lensOptions : accessoryOptions)
    .filter(p => p.label.toLowerCase().includes(productSearch.toLowerCase()));

  const handleExport = async () => {
    try {
      const response = await quotesAPI.exportQuotes();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'quotes.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Erreur lors de l'export des devis");
    }
  };

  const generateDevisPDF = async (quote) => {
    try {
      const res = await quotesAPI.getQuote(quote.id);
      const fullQuote = res.data;
      const clientName = getClientName(fullQuote.client_id);
      const enhancedItems = (fullQuote.items || []).map((item) => ({
        ...item,
        product_name: getProductName(item.product_id),
        price: item.price
      }));
      generateQuotePDF(fullQuote, clientName, enhancedItems);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const itemsPerPage = 10;
  const paginatedQuotes = filteredQuotes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredQuotes.length / itemsPerPage);

  // New functions for status workflow
  const handleAcceptQuote = async (quote) => {
    if (!window.confirm('Êtes-vous sûr de vouloir accepter ce devis ? Cette action est irréversible.')) return;
    try {
      await quotesAPI.updateQuote(quote.id, { status: 'accepted' });
      toast.success('Devis accepté');
      fetchQuotes();
    } catch (err) {
      toast.error('Erreur lors de l\'acceptation du devis');
    }
  };

  const handleRejectQuote = async (quote) => {
    if (!window.confirm('Êtes-vous sûr de vouloir rejeter ce devis ? Cette action est irréversible.')) return;
    try {
      await quotesAPI.updateQuote(quote.id, { status: 'rejected' });
      toast.success('Devis rejeté');
      fetchQuotes();
    } catch (err) {
      toast.error('Erreur lors du rejet du devis');
    }
  };

  const handleConvertToSale = async (quote) => {
    if (!window.confirm('Êtes-vous sûr de vouloir convertir ce devis en vente ? Cette action est irréversible.')) return;
    try {
      await quotesAPI.updateQuote(quote.id, { status: 'accepted' }); // Assuming 'accepted' means 'sold'
      toast.success('Devis converti en vente');
      fetchQuotes();
    } catch (err) {
      toast.error('Erreur lors de la conversion en vente');
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Devis (Ventes)</h1>
            <p className="text-neutral-600">Gérez les devis de vente</p>
          </div>
          <div className="flex items-center space-x-3">
            <ModernSearchBar
              placeholder="Rechercher des devis..."
              onSearch={setSearchTerm}
              className="w-64"
              debounce={300}
            />
            <ModernButton
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={handleAdd}
            >
              Ajouter un devis
            </ModernButton>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <ModernCard className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Total Devis</p>
                <p className="text-2xl font-bold text-neutral-900">{quotes.length}</p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary-600" />
              </div>
            </div>
          </ModernCard>

          <ModernCard className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Montant Total</p>
                <p className="text-2xl font-bold text-success-600">
                  {formatCurrency(totalAmount)}
                </p>
              </div>
              <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-success-600" />
              </div>
            </div>
          </ModernCard>

          <ModernCard className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">En Attente</p>
                <p className="text-2xl font-bold text-warning-600">{pendingQuotes}</p>
              </div>
              <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-warning-600" />
              </div>
            </div>
          </ModernCard>

          <ModernCard className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600">Acceptés</p>
                <p className="text-2xl font-bold text-info-600">{acceptedQuotes}</p>
              </div>
              <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-info-600" />
              </div>
            </div>
          </ModernCard>
        </div>

        {/* Quotes Table */}
        <ModernCard className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Liste des devis</h3>
              <p className="text-sm text-neutral-600">{filteredQuotes.length} devis trouvés</p>
            </div>
            <div className="flex items-center space-x-2">
              <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
                Filtrer
              </ModernButton>
              <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />} onClick={handleExport} title="Exporter les devis au format CSV">
                Exporter
              </ModernButton>
            </div>
          </div>

          <ModernTable
            columns={tableColumns}
            data={paginatedQuotes}
            className="w-full"
          />
          {paginatedQuotes.length === 0 && (
            <div className="text-center text-neutral-400 py-8">Aucun devis trouvé.</div>
          )}
          {totalPages > 1 && (
            <div className="flex justify-center mt-4 space-x-2">
              <ModernButton variant="secondary" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Précédent</ModernButton>
              <span className="px-2 py-1">Page {currentPage} / {totalPages}</span>
              <ModernButton variant="secondary" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Suivant</ModernButton>
            </div>
          )}
        </ModernCard>

        {/* Add/Edit Modal */}
        <ModernModal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          title={editingId ? 'Modifier le devis' : 'Ajouter un devis'}
          size="full"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Replace ModernSelect for Client with EntitySelectWithAdd */}
              <EntitySelectWithAdd
                entityType="client"
                label="Client"
                options={clientOptions}
                value={form.client_id}
                onChange={value => setForm(f => ({ ...f, client_id: value }))}
                fetchOptions={fetchClients} // assumes you have a fetchClients function
                required
                showAddButton={true}
              />

              <ModernInput
                label="Date"
                name="date"
                type="date"
                value={form.date}
                onChange={handleChange}
                icon={<Calendar className="w-5 h-5" />}
                required
              />

              <ModernSelect
                label="Statut"
                name="status"
                value={statusOptions.find(opt => opt.value === form.status) || null}
                onChange={option => setForm(f => ({ ...f, status: option ? option.value : '' }))}
                options={statusOptions}
              />
              <ModernInput
                label="Note"
                name="note"
                value={form.note}
                onChange={handleChange}
                placeholder="Ajouter une note (optionnel)"
              />
            </div>
            {/* Product selection section */}
            <div className="space-y-4 border-t pt-4 mt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Produits</h3>
                <ModernButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  icon={<Plus className="w-4 h-4" />}
                  onClick={() => openModal('monture')}
                >
                  Ajouter un produit
                </ModernButton>
              </div>
              
              {/* Display added products */}
              {form.items && form.items.length > 0 ? (
                <div className="space-y-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">{getProductName(item.product_id)}</div>
                        <div className="text-sm text-gray-600">
                          Quantité: {item.quantity} | Prix: {formatCurrency(item.price)} | Total: {formatCurrency(getItemTotal(item))}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <ModernButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          icon={<Edit className="w-4 h-4" />}
                          onClick={() => openModal(item.item_type, idx)}
                        >
                          Modifier
                        </ModernButton>
                        <ModernButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          icon={<Trash2 className="w-4 h-4" />}
                          onClick={() => removeItem(idx)}
                        >
                          Supprimer
                        </ModernButton>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Aucun produit ajouté
                </div>
              )}
            </div>

            <div className="flex items-center justify-end mt-4">
              <span className="text-lg font-bold text-success-700">Total: {formatCurrency(calculatedTotal)} MAD</span>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <ModernButton
                type="button"
                variant="secondary"
                onClick={() => setShowForm(false)}
              >
                Annuler
              </ModernButton>
              <ModernButton
                type="submit"
                variant="primary"
                icon={<Save className="w-4 h-4" />}
                disabled={submitLoading}
              >
                {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
              </ModernButton>
            </div>
          </form>
        </ModernModal>

        {/* Product Item Modal - OUTSIDE the main form/modal */}
        <ModernModal
          isOpen={modalOpen}
          onClose={closeModal}
          title={modalEditIdx !== null ? 'Modifier le produit' : 'Ajouter un produit'}
          size="md"
        >
          <div className="space-y-4">
            {/* Product Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher un produit
              </label>
              <div className="relative">
                <ModernInput
                  placeholder="Tapez pour rechercher..."
                  value={productSearchTerm}
                  onChange={(e) => {
                    setProductSearchTerm(e.target.value);
                  }}
                  icon={<Search className="w-5 h-5" />}
                />
                <button
                  type="button"
                  onClick={() => setShowAddProductModal(true)}
                  className="absolute right-2 top-2 h-8 w-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                  title="Ajouter un nouveau produit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Product Selection */}
            {productSearchTerm && (
              <div className="max-h-60 overflow-y-auto border rounded-lg">
                {filterProducts(productSearchTerm).map(product => (
                  <div
                    key={product.id}
                    className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                      modalForm.product_id === product.id ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                    onClick={() => {
                      handleModalChange('product_id', product.id);
                      handleModalChange('price', product.sale_price || 0);
                      setProductSearchTerm(product.name);
                    }}
                  >
                    <div className="font-medium">{product.name || 'Produit sans nom'}</div>
                                      <div className="text-sm text-gray-600">
                    Réf: {product.model || 'N/A'} | Prix: {formatCurrency(product.sale_price || 0)}
                  </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quantity and Price */}
            <div className="grid grid-cols-2 gap-4">
              <ModernInput
                label="Quantité"
                type="number"
                min="1"
                value={modalForm.quantity}
                onChange={(e) => handleModalChange('quantity', parseInt(e.target.value) || 1)}
                required
              />
              <ModernInput
                label="Prix unitaire"
                type="number"
                min="0"
                step="0.01"
                value={modalForm.price}
                onChange={(e) => handleModalChange('price', parseFloat(e.target.value) || 0)}
                required
              />
            </div>

            {modalError && (
              <div className="text-red-600 text-sm">{modalError}</div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-4">
              <ModernButton
                type="button"
                variant="secondary"
                onClick={closeModal}
              >
                Annuler
              </ModernButton>
              <ModernButton
                type="button"
                variant="primary"
                onClick={handleModalSave}
              >
                {modalEditIdx !== null ? 'Modifier' : 'Ajouter'}
              </ModernButton>
            </div>
          </div>
        </ModernModal>
      </div>

      {/* Details Modal */}
      <ModernModal isOpen={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} title="Détails du devis" size="lg">
        {detailsLoading ? (
          <div className="flex items-center justify-center min-h-[200px]"><ModernLoadingSpinner size="lg" /></div>
        ) : selectedDevis && (
          <div className="space-y-2">
            <div><b>ID:</b> {selectedDevis.id}</div>
            <div><b>Client:</b> {getClientName(selectedDevis.client_id)}</div>
            <div><b>Montant:</b> {formatCurrency(selectedDevis.amount)}</div>
            <div><b>Date:</b> {formatDate(selectedDevis.date)}</div>
            <div><b>Statut:</b> {getStatusBadge(selectedDevis.status)}</div>
            <div><b>Note:</b> {selectedDevis.note || '-'} </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <ModernButton
                variant="primary"
                size="sm"
                icon={<FileDown className="w-4 h-4" />}
                onClick={() => generateDevisPDF(selectedDevis)}
                className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700"
              >
                PDF
              </ModernButton>
              <ModernButton variant="success" size="sm" icon={<CheckCircle className="w-4 h-4" />} onClick={() => handleAcceptQuote(selectedDevis)} disabled={selectedDevis.status === 'accepted'}>Accepter</ModernButton>
              <ModernButton variant="danger" size="sm" icon={<XCircle className="w-4 h-4" />} onClick={() => handleRejectQuote(selectedDevis)} disabled={selectedDevis.status === 'rejected'}>Rejeter</ModernButton>
              <ModernButton variant="info" size="sm" icon={<ShoppingCart className="w-4 h-4" />} onClick={() => handleConvertToSale(selectedDevis)} disabled={selectedDevis.status !== 'accepted'}>Convertir en vente</ModernButton>
            </div>
          </div>
        )}
      </ModernModal>

      {/* Add Product Modal */}
      <ModernModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        title="Ajouter un nouveau produit"
        size="lg"
      >
        <AddProductForm
          onSuccess={handleProductCreated}
          onCancel={() => setShowAddProductModal(false)}
        />
      </ModernModal>
    </>
  );
};

export default VentesDevis; 